import type { SyntheticEvent, VFC } from 'react'
import { useMemo, useState } from 'react'
import { Image } from '@opallabs/lapidary'
import { assetProxyImageUrl, assetProxyPreviewUrl } from '@opallabs/routing'
import { DEV_UUID } from '@opallabs/starman'
import placeholder from '../placeholderImage'
import { useImageTransformDefaults } from './defaults'
import type { ImageProxyProps } from './types'

interface AssetProxyParams {
  cache?: boolean
  height: number | null
  width: number | null
  uuid: string
}

// Check if the asset is a TIFF file that requires server-side transcoding
const isTiffFile = (
  assetReference: ImageProxyProps['assetReference']
): boolean => {
  return (
    /\.tiff?/.test(assetReference.filename?.toLowerCase() ?? '') ||
    /^tiff?$/.test(assetReference.format ?? '')
  )
}

// Build preview URL with optional proportional height calculation
const buildPreviewUrl = (
  params: AssetProxyParams,
  assetReference: ImageProxyProps['assetReference']
): string => {
  // For width-only transforms, calculate proportional height to maintain aspect ratio
  if (
    params.width &&
    !params.height &&
    assetReference.width &&
    assetReference.height &&
    assetReference.width > 0
  ) {
    const proportionalHeight = Math.round(
      (params.width / assetReference.width) * assetReference.height
    )
    return assetProxyPreviewUrl({
      ...params,
      height: proportionalHeight,
    })
  }

  return assetProxyPreviewUrl(params)
}

const ImageProxy: VFC<ImageProxyProps> = ({
  alt,
  ariaLabel,
  ariaMode,
  assetReference,
  automaticallyScaleDPI = true,
  imageFit,
  onClick,
  onError,
  onLoad,
  preview = false,
  styleOverrides,
  transcodingUnavailable = false,
  transforms = {},
}) => {
  const [usePlaceholder, setUsePlaceholder] = useState(
    preview && transcodingUnavailable
  )
  const transformDefaults = useImageTransformDefaults()

  const assetProxyUrl = useMemo(() => {
    if (!assetReference.uuid) return null
    const dpi = automaticallyScaleDPI ? window.devicePixelRatio : 1
    const appliedTransforms = { ...transformDefaults, ...transforms }

    // TIFF files require server-side transcoding to a web-compatible format
    const needsTranscoding = isTiffFile(assetReference)

    // Use standard transform logic, but route TIFF files to preview URL for transcoding
    const params: AssetProxyParams = {
      height: appliedTransforms.height ? appliedTransforms.height * dpi : null,
      width: appliedTransforms.width ? appliedTransforms.width * dpi : null,
      uuid: assetReference.uuid,
    }

    // For TIFF files with no transforms, use natural dimensions
    if (
      needsTranscoding &&
      !appliedTransforms.width &&
      !appliedTransforms.height
    ) {
      params.width = assetReference.width ? assetReference.width * dpi : null
      params.height = assetReference.height ? assetReference.height * dpi : null
    }

    // Route selection based on asset type and mode
    // Allows PDFs to render itself as an image
    if (assetReference.format === 'pdf') {
      return assetProxyImageUrl({
        width: params.width ?? 900,
        height: params.height ?? 9999,
        uuid: assetReference.uuid ?? '',
        queryParams: appliedTransforms,
      })
    }

    // Allows images to render in Storybook
    if (assetReference.uuid === DEV_UUID) {
      return assetReference.full_url || ''
    }

    // If we need to force a preview/transcoding of the asset to preview it
    if (preview || needsTranscoding) {
      return buildPreviewUrl(params, assetReference)
    }

    return assetProxyImageUrl({ ...params, queryParams: appliedTransforms })
  }, [
    assetReference,
    automaticallyScaleDPI,
    preview,
    transformDefaults,
    transforms,
  ])

  if (!assetReference.uuid) return null

  const handleError = (error: SyntheticEvent<HTMLImageElement>) => {
    setUsePlaceholder(true)
    onError?.(error)
  }

  return (
    <Image
      alt={
        alt ||
        assetReference.description ||
        assetReference.title ||
        assetReference.filename ||
        ''
      }
      ariaLabel={ariaLabel}
      ariaMode={ariaMode}
      imageFit={imageFit}
      onClick={onClick}
      onError={handleError}
      onLoad={onLoad}
      src={usePlaceholder ? placeholder : assetProxyUrl || ''}
      styleOverrides={styleOverrides}
      title={assetReference?.title}
    />
  )
}
export { ImageProxy }
